import React, { useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { cellText, normalizeToMs } from "../../utils/helpers";
import CustomSelect from "../../components/CustomSelect";
import * as XLSX from "xlsx";

// Per-employee analysis. Everything is derived from records already in memory —
// no API calls, no queries. Status comes straight from "Overall Status", which
// the worker already computes, so these numbers match the task pages exactly.

const IST = 330 * 60 * 1000;
const EXCEL_EPOCH = 25569;
const DATE_FMT = "dd-mmm-yyyy";

const COLORS = {
  Done: "#12B76A",
  Overdue: "#F04438",
  "In Progress": "#F79009",
  "Not Started": "#98A2B3",
};

// A person can appear on a task in three different roles; match on open_id so a
// rename in Lark never breaks the lookup.
function fieldHasId(field, id) {
  if (!field || !id) return false;
  const arr = Array.isArray(field) ? field : [field];
  return arr.some(v => String(v?.id || v?.open_id || "") === String(id));
}

function statusOf(fields) {
  const s = cellText(fields?.["Overall Status"]) || "Not Started";
  const l = s.toLowerCase();
  if (l.includes("done") || l.includes("✅")) return "Done";
  if (l.includes("overdue") || l.includes("🔴")) return "Overdue";
  if (l.includes("progress") || l.includes("🟡")) return "In Progress";
  return "Not Started";
}

function fmtDate(ms) {
  if (!ms) return "—";
  const d = new Date(ms + IST);
  const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${String(d.getUTCDate()).padStart(2,"0")} ${mo[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
const toSerial = (ms) => (ms ? (ms + IST) / 86400000 + EXCEL_EPOCH : "");

/* ── tiny SVG charts (no chart library in this project) ────────────── */

function Donut({ data, size = 170 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return <div className="an-empty">No data</div>;
  const r = size / 2 - 16, cx = size / 2, cy = size / 2, C = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
      {data.filter(d => d.value > 0).map((d, i) => {
        const frac = d.value / total;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={d.color} strokeWidth="26"
            strokeDasharray={`${frac * C} ${C - frac * C}`}
            strokeDashoffset={-offset * C}
            transform={`rotate(-90 ${cx} ${cy})`}>
            <title>{`${d.label}: ${d.value} (${(frac * 100).toFixed(1)}%)`}</title>
          </circle>
        );
        offset += frac;
        return el;
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="24" fontWeight="800" fill="var(--text-primary)">{total}</text>
      <text x={cx} y={cy + 15} textAnchor="middle" fontSize="10" fontWeight="600" fill="var(--text-tertiary)">TASKS</text>
    </svg>
  );
}

function HBars({ items, color = "var(--primary)" }) {
  const max = Math.max(1, ...items.map(i => i.value));
  if (!items.length) return <div className="an-empty">No data</div>;
  return (
    <div className="an-bars">
      {items.map((i, k) => (
        <div className="an-bar-row" key={k}>
          <span className="an-bar-label" title={i.label}>{i.label}</span>
          <div className="an-bar-track">
            <div className="an-bar-fill" style={{ width: `${(i.value / max) * 100}%`, background: i.color || color }} />
          </div>
          <span className="an-bar-val">{i.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function VBars({ groups }) {
  const max = Math.max(1, ...groups.flatMap(g => g.bars.map(b => b.value)));
  if (!groups.length) return <div className="an-empty">No data</div>;
  return (
    <div className="an-vwrap">
      <div className="an-vbars">
        {groups.map((g, i) => (
          <div className="an-vgroup" key={i}>
            <div className="an-vstack">
              {g.bars.map((b, j) => (
                <div key={j} className="an-vbar"
                  style={{ height: `${(b.value / max) * 100}%`, background: b.color }}
                  title={`${g.label} — ${b.label}: ${b.value}`} />
              ))}
            </div>
            <span className="an-vlabel">{g.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── page ──────────────────────────────────────────────────────────── */

export default function AnalyzePage() {
  const { adminRecords, userRecords, larkUsers, loading } = useData();
  const [employeeId, setEmployeeId] = useState("");
  const [role, setRole] = useState("auto");
  const [pdfFullList, setPdfFullList] = useState(false);

  const employee = useMemo(
    () => (larkUsers || []).find(u => String(u.id) === String(employeeId)) || null,
    [larkUsers, employeeId]
  );

  // The same person shows up in three places; count all three so you can see
  // at a glance which one actually describes their job.
  const roleSets = useMemo(() => {
    if (!employee) return { assigned: [], supervising: [], created: [] };
    const id = employee.id;
    const assignedTaskIds = new Set(
      (userRecords || []).filter(r => String(r.user_id) === String(id))
        .map(r => cellText(r.fields?.["Task ID"])).filter(Boolean)
    );
    const assigned = (adminRecords || []).filter(r => {
      const f = r.fields || {};
      return assignedTaskIds.has(cellText(f["Task ID"])) || fieldHasId(f["Assigned Users"], id);
    });
    const supervising = (adminRecords || []).filter(r => fieldHasId(r.fields?.["Supervisor"], id));
    const created = (adminRecords || []).filter(r => {
      const f = r.fields || {};
      return fieldHasId(f["Task Creator"], id) || fieldHasId(f["Assigned By"], id);
    });
    return { assigned, supervising, created };
  }, [employee, adminRecords, userRecords]);

  // Team-wide roll-up shown before anyone is picked. Built in a single pass so
  // 10k tasks x 40 people stays cheap; memoised on the record sets.
  const teamStats = useMemo(() => {
    const byUser = {};
    const touch = (id) => (byUser[id] = byUser[id] || {
      assigned: 0, done: 0, overdue: 0, pending: 0, onTime: 0, late: 0, supervising: 0, created: 0,
    });
    const overall = { Done: 0, Overdue: 0, "In Progress": 0, "Not Started": 0 };

    for (const r of (adminRecords || [])) {
      const f = r.fields || {};
      const st = statusOf(f);
      overall[st] = (overall[st] || 0) + 1;
      const end = normalizeToMs(f["End Date"]);
      const comp = normalizeToMs(f["Completed At"] || f["Completion Time"]);
      const timely = st === "Done" && comp && end ? (comp > end + 60000 ? "late" : "onTime") : null;

      const people = (x) => (Array.isArray(x) ? x : x ? [x] : []);
      for (const u of people(f["Assigned Users"])) {
        const id = u?.id || u?.open_id; if (!id) continue;
        const b = touch(id); b.assigned++;
        if (st === "Done") b.done++; else if (st === "Overdue") b.overdue++; else b.pending++;
        if (timely) b[timely]++;
      }
      for (const u of people(f["Supervisor"])) { const id = u?.id || u?.open_id; if (id) touch(id).supervising++; }
      for (const u of people(f["Task Creator"]).concat(people(f["Assigned By"]))) {
        const id = u?.id || u?.open_id; if (id) touch(id).created++;
      }
    }

    const rows = (larkUsers || []).map(u => ({ id: u.id, name: u.name, ...(byUser[u.id] || {
      assigned: 0, done: 0, overdue: 0, pending: 0, onTime: 0, late: 0, supervising: 0, created: 0 }) }))
      .filter(u => u.assigned || u.supervising || u.created)
      .sort((a, b) => (b.assigned - a.assigned) || (b.supervising - a.supervising));
    return { rows, overall, totalTasks: (adminRecords || []).length };
  }, [adminRecords, larkUsers]);

  const activeRole = useMemo(() => {
    if (role !== "auto") return role;
    const { assigned, supervising, created } = roleSets;
    const best = Math.max(assigned.length, supervising.length, created.length);
    if (!best) return "assigned";
    if (supervising.length === best) return "supervising";
    if (assigned.length === best) return "assigned";
    return "created";
  }, [role, roleSets]);

  const rows = roleSets[activeRole] || [];

  const stats = useMemo(() => {
    const now = Date.now();
    const byStatus = { Done: 0, Overdue: 0, "In Progress": 0, "Not Started": 0 };
    const byDept = {}, byPriority = {}, byType = {}, byMonth = {};
    let onTime = 0, late = 0, lateBuckets = { "Within a week": 0, "1–4 weeks": 0, "Over a month": 0 };
    const detail = [];

    for (const r of rows) {
      const f = r.fields || {};
      const st = statusOf(f);
      byStatus[st] = (byStatus[st] || 0) + 1;

      const dept = cellText(f["Assignee Department"]) || "—";
      byDept[dept] = byDept[dept] || { total: 0, done: 0, overdue: 0, pending: 0 };
      byDept[dept].total++;
      if (st === "Done") byDept[dept].done++;
      else if (st === "Overdue") byDept[dept].overdue++;
      else byDept[dept].pending++;

      const pri = cellText(f["Priority"]) || "Medium";
      byPriority[pri] = (byPriority[pri] || 0) + 1;
      const rt = cellText(f["Recurring Type"]);
      const type = rt && rt !== "—" && rt !== "Not Applicable" ? rt : "One-off";
      byType[type] = (byType[type] || 0) + 1;

      const end = normalizeToMs(f["End Date"]);
      const comp = normalizeToMs(f["Completed At"] || f["Completion Time"]);
      if (end) {
        const d = new Date(end + IST);
        const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
        byMonth[key] = byMonth[key] || { due: 0, done: 0, overdue: 0 };
        byMonth[key].due++;
        if (st === "Done") byMonth[key].done++;
        if (st === "Overdue") byMonth[key].overdue++;
      }
      if (st === "Done" && comp && end) {
        if (comp > end + 60000) {
          late++;
          const days = (comp - end) / 86400000;
          lateBuckets[days < 7 ? "Within a week" : days < 30 ? "1–4 weeks" : "Over a month"]++;
        } else onTime++;
      }

      detail.push({
        id: cellText(f["Task ID"]), name: cellText(f["Task Name"]) || "Untitled",
        status: st, priority: pri, dept, type, end, comp,
        // Who the task is actually on — shown in the exports in place of the
        // department column.
        assignees: (() => {
          const raw = f["Total Assigned Users"] || f["All Assigned Users"] || f["Assigned Users"] || f["User"];
          if (Array.isArray(raw)) {
            const n = raw.map(v => v?.en_name || v?.name).filter(Boolean);
            if (n.length) return n.join(", ");
          }
          const str = cellText(raw);
          return str && str !== "—" ? str : "";
        })(),
        daysOverdue: st === "Overdue" && end ? Math.floor((now - end) / 86400000) : "",
        daysLeft: st !== "Done" && st !== "Overdue" && end ? Math.ceil((end - now) / 86400000) : "",
        ratio: `${(f["Completed Count Copy"]?.length) || 0}/${(Array.isArray(f["Assigned Users"]) ? f["Assigned Users"].length : 0)}`,
      });
    }
    return { byStatus, byDept, byPriority, byType, byMonth, onTime, late, lateBuckets, detail };
  }, [rows]);

  const total = rows.length;
  const pending = stats.byStatus["Not Started"] + stats.byStatus["In Progress"];
  const doneN = stats.byStatus.Done;
  // Only tasks carrying BOTH a due date and a completion time can be judged for
  // timeliness — anything missing a timestamp is left out of the ratio rather
  // than silently counted as on time.
  const rated = stats.onTime + stats.late;

  // Mirrors the structure of the standalone report: Summary, Timeliness,
  // By Department, Priority & Type, Monthly Trend, Overdue, Pending, All Tasks.
  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    const pct = (n, d) => (d ? `${((n / d) * 100).toFixed(1)}%` : "—");
    const addSheet = (name, aoa, cols, dateCols = [], filterCols = 0, titleSpan = 0) => {
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = cols.map(w => ({ wch: w }));
      if (titleSpan) ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: titleSpan - 1 } },
                                      { s: { r: 1, c: 0 }, e: { r: 1, c: titleSpan - 1 } }];
      for (const c of dateCols) {
        for (let r = 1; r < aoa.length; r++) {
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          if (cell && cell.t === "n") cell.z = DATE_FMT;
        }
      }
      if (filterCols) ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(0, aoa.length - 1), c: filterCols - 1 } }) };
      XLSX.utils.book_append_sheet(wb, ws, name);
    };

    // ── team-wide export when nobody is selected ──
    if (!employee) {
      const t = teamStats;
      const pend = t.overall["Not Started"] + t.overall["In Progress"];
      addSheet("Summary", [
        ["Team Task Summary"], [`Generated ${fmtDate(Date.now())}`], [],
        ["Metric", "Value", "Share"],
        ["Tasks", t.totalTasks, ""],
        ["Completed", t.overall.Done, pct(t.overall.Done, t.totalTasks)],
        ["Overdue", t.overall.Overdue, pct(t.overall.Overdue, t.totalTasks)],
        ["Pending", pend, pct(pend, t.totalTasks)],
        ["People with tasks", t.rows.length, ""],
      ], [30, 14, 12]);
      addSheet("By Employee", [
        ["Employee", "Assigned", "Done", "Overdue", "Pending", "On time %", "Supervising", "Created"],
        ...t.rows.map(u => [u.name, u.assigned, u.done, u.overdue, u.pending,
          u.onTime + u.late ? Number(((u.onTime / (u.onTime + u.late)) * 100).toFixed(1)) : "", u.supervising, u.created]),
      ], [28, 11, 10, 11, 11, 11, 12, 10], [], 8);
      XLSX.writeFile(wb, `Team_Analysis_${new Date().toISOString().slice(0, 10)}.xlsx`);
      return;
    }

    if (!total) return;
    const rated = stats.onTime + stats.late;
    const roleLabel = { assigned: "Assignee", supervising: "Supervisor", created: "Creator" }[activeRole];

    addSheet("Summary", [
      [`Task Summary — ${employee.name}`],
      [`Role: ${roleLabel}  ·  Generated ${fmtDate(Date.now())}  ·  Source: live task database`], [],
      ["Metric", "Value", "Share"],
      ["Tasks", total, ""],
      ["Completed", doneN, pct(doneN, total)],
      ["Overdue (open, past due)", stats.byStatus.Overdue, pct(stats.byStatus.Overdue, total)],
      ["Pending (not yet due)", pending, pct(pending, total)],
      ["— of completed: on time", stats.onTime, pct(stats.onTime, rated)],
      ["— of completed: late", stats.late, pct(stats.late, rated)],
      ["— completion time not recorded", doneN - rated, ""],
      [],
      ["Status", "Tasks", "Share"],
      ...["Done", "Overdue", "In Progress", "Not Started"].map(k =>
        [k, stats.byStatus[k] || 0, pct(stats.byStatus[k] || 0, total)]),
    ], [34, 14, 12], [], 0, 4);

    addSheet("Timeliness", [
      ["How late were the completed tasks?"], [`On time ${stats.onTime} · Late ${stats.late} · of ${rated} timed`], [],
      ["Lateness", "Tasks", "Share of late"],
      ...Object.entries(stats.lateBuckets).map(([k, v]) => [k, v, pct(v, stats.late)]),
    ], [24, 12, 16], [], 0, 3);

    addSheet("By Department", [
      ["Department", "Total", "Done", "Overdue", "Pending", "% complete"],
      ...Object.entries(stats.byDept).sort((a, b) => b[1].total - a[1].total)
        .map(([k, v]) => [k, v.total, v.done, v.overdue, v.pending, pct(v.done, v.total)]),
    ], [28, 10, 10, 11, 11, 12], [], 6);

    addSheet("Priority & Type", [
      ["Priority", "Tasks"],
      ...Object.entries(stats.byPriority).sort((a, b) => b[1] - a[1]),
      [], ["Task type", "Tasks"],
      ...Object.entries(stats.byType).sort((a, b) => b[1] - a[1]),
    ], [20, 12]);

    const months = Object.keys(stats.byMonth).sort();
    addSheet("Monthly Trend", [
      ["Month", "Tasks due", "Completed", "Overdue"],
      ...months.map(m => [m, stats.byMonth[m].due, stats.byMonth[m].done, stats.byMonth[m].overdue]),
    ], [14, 12, 12, 11], [], 4);

    const byNewest = (a, b) => (b.end || 0) - (a.end || 0);
    const ov = stats.detail.filter(t => t.status === "Overdue").sort(byNewest);
    addSheet("Overdue (action)", [
      ["Task ID", "Task", "Assigned Users", "Priority", "Due date", "Days overdue", "Done / Total"],
      ...ov.map(t => [t.id, t.name, t.assignees, t.priority, toSerial(t.end), t.daysOverdue, t.ratio]),
    ], [12, 46, 30, 11, 14, 14, 13], [4], 7);

    const pd = stats.detail.filter(t => t.status === "Not Started" || t.status === "In Progress").sort(byNewest);
    addSheet("Pending (action)", [
      ["Task ID", "Task", "Assigned Users", "Priority", "Due date", "Days until due", "Status", "Done / Total"],
      ...pd.map(t => [t.id, t.name, t.assignees, t.priority, toSerial(t.end), t.daysLeft, t.status, t.ratio]),
    ], [12, 46, 30, 11, 14, 15, 14, 13], [4], 8);

    addSheet("All Tasks", [
      ["Task ID", "Task", "Status", "Priority", "Assigned Users", "Type", "Due date", "Completed", "Done / Total"],
      ...[...stats.detail].sort(byNewest).map(t =>
        [t.id, t.name, t.status, t.priority, t.assignees, t.type, toSerial(t.end), toSerial(t.comp), t.ratio]),
    ], [12, 46, 17, 11, 30, 12, 14, 14, 12], [6, 7], 9);

    const safe = employee.name.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    XLSX.writeFile(wb, `${safe}_Task_Summary_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading && adminRecords.length === 0) {
    return <div className="loading-box"><div className="spinner" /><span>Loading...</span></div>;
  }

  const months = Object.keys(stats.byMonth).sort();
  const deptItems = Object.entries(stats.byDept).sort((a, b) => b[1].total - a[1].total)
    .slice(0, 8).map(([k, v]) => ({ label: k, value: v.total }));

  return (
    <div className="fade-in">
      <style>{`
        .an-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:16px; }
        .an-card { background:var(--surface-0); border:1px solid var(--border-default); border-radius:var(--radius-lg);
                   padding:16px 18px; box-shadow:var(--shadow-sm); }
        .an-card h3 { margin:0 0 14px; font-size:13px; font-weight:800; color:var(--text-secondary);
                      letter-spacing:.04em; text-transform:uppercase; }
        .an-kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:12px; margin-bottom:18px; }
        .an-kpi { background:var(--surface-0); border:1px solid var(--border-default); border-radius:var(--radius-lg);
                  padding:14px 16px; box-shadow:var(--shadow-xs); }
        .an-kpi-num { font-size:26px; font-weight:800; line-height:1.1; font-variant-numeric:tabular-nums; }
        .an-kpi-lab { font-size:11.5px; font-weight:700; color:var(--text-tertiary); margin-top:4px;
                      text-transform:uppercase; letter-spacing:.04em; }
        .an-kpi-sub { font-size:11px; color:var(--text-tertiary); margin-top:2px; }
        .an-donut-wrap { display:flex; align-items:center; gap:18px; flex-wrap:wrap; }
        .an-legend { display:flex; flex-direction:column; gap:7px; flex:1; min-width:140px; }
        .an-leg { display:flex; align-items:center; gap:8px; font-size:12.5px; }
        .an-dot { width:10px; height:10px; border-radius:3px; flex-shrink:0; }
        .an-leg-lab { flex:1; color:var(--text-secondary); font-weight:600; }
        .an-leg-val { font-weight:800; font-variant-numeric:tabular-nums; }
        .an-bars { display:flex; flex-direction:column; gap:9px; }
        .an-bar-row { display:flex; align-items:center; gap:10px; font-size:12.5px; }
        .an-bar-label { width:120px; flex-shrink:0; color:var(--text-secondary); font-weight:600;
                        overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .an-bar-track { flex:1; height:16px; background:var(--surface-2); border-radius:4px; overflow:hidden; }
        .an-bar-fill { height:100%; border-radius:4px; transition:width .35s ease; }
        .an-bar-val { width:52px; text-align:right; font-weight:800; font-variant-numeric:tabular-nums; }
        .an-vwrap { overflow-x:auto; }
        .an-vbars { display:flex; gap:12px; align-items:flex-end; height:180px; min-width:min-content; padding-top:8px; }
        .an-vgroup { display:flex; flex-direction:column; align-items:center; gap:6px; min-width:46px; }
        .an-vstack { display:flex; gap:3px; align-items:flex-end; height:150px; }
        .an-vbar { width:13px; border-radius:3px 3px 0 0; min-height:2px; }
        .an-vlabel { font-size:10.5px; color:var(--text-tertiary); font-weight:700; white-space:nowrap; }
        .an-empty { padding:28px 0; text-align:center; color:var(--text-disabled); font-size:12.5px; }
        .an-roles { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:16px; }
        .an-role { padding:8px 14px; border:1px solid var(--border-default); border-radius:var(--radius);
                   background:var(--surface-1); color:var(--text-tertiary); font-size:12.5px; font-weight:700;
                   font-family:inherit; cursor:pointer; display:flex; align-items:center; gap:7px; }
        .an-role.active { background:var(--primary); border-color:var(--primary); color:var(--surface-0); }
        .an-role-n { padding:1px 7px; border-radius:var(--radius-pill); background:var(--surface-3);
                     font-size:11px; font-weight:800; color:var(--text-secondary); }
        .an-role.active .an-role-n { background:rgba(255,255,255,.25); color:var(--surface-0); }
        .an-table { width:100%; border-collapse:collapse; font-size:12.5px; }
        .an-table th { padding:9px 10px; text-align:right; font-size:10.5px; font-weight:800;
                       color:var(--text-tertiary); text-transform:uppercase; letter-spacing:.04em;
                       border-bottom:1px solid var(--border-default); white-space:nowrap; }
        .an-table td { padding:9px 10px; text-align:right; border-bottom:1px solid var(--border-subtle);
                       font-variant-numeric:tabular-nums; white-space:nowrap; }
        .an-table tbody tr { cursor:pointer; transition:background .12s; }
        .an-table tbody tr:hover { background:var(--surface-2); }
        @media (max-width:768px){ .an-bar-label{width:86px;} }

        /* ── PDF / print ──────────────────────────────────────────────
           Only applies while printing. The charts are already inline SVG,
           so they come through as crisp vectors rather than screenshots. */
        @media print {
          @page { size: A4 portrait; margin: 12mm 10mm; }
          .sidebar, .sidebar-overlay, .topbar, .hamburger { display: none !important; }
          .layout { display: block !important; }
          .main-content { margin: 0 !important; width: 100% !important; }
          .page-content { padding: 0 !important; overflow: visible !important; }
          body { background: #fff !important; }
          .an-no-print { display: none !important; }
          .an-print-head { display: block !important; }
          .an-card, .an-kpi {
            box-shadow: none !important;
            border: 1px solid #D0D5DD !important;
            break-inside: avoid; page-break-inside: avoid;
          }
          .an-grid { display: block !important; }
          .an-card { margin-bottom: 10mm; }
          .an-kpis { display: grid !important; grid-template-columns: repeat(5,1fr) !important; }
          .an-table { font-size: 9pt; }
          .an-table tbody tr { break-inside: avoid; }
          /* keep the colours — browsers strip backgrounds when printing by default */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .an-print-only { display: none; }
        @media print {
          .an-print-only { display: block !important; }
          .an-sheet { break-before: page; page-break-before: always; }
          .an-sheet:first-of-type { break-before: auto; page-break-before: auto; }
          .an-sheet h4 { font-size: 13pt; font-weight: 800; color: #1E3A5F; margin: 0 0 2mm; }
          .an-sheet p.sub { font-size: 8.5pt; color: #667085; margin: 0 0 3mm; }
          .an-ptable { width: 100%; border-collapse: collapse; font-size: 7.5pt;
                       table-layout: fixed; }
          .an-ptable td, .an-ptable th {
            overflow-wrap: anywhere; word-break: break-word;
            white-space: normal !important; text-overflow: clip !important;
            overflow: visible !important; vertical-align: top;
          }
          .an-ptable th { line-height: 1.15; }
          .an-ptable th { background: #1E3A5F; color: #fff; padding: 4px 6px; text-align: left;
                          font-weight: 700; font-size: 7.5pt; border: 1px solid #1E3A5F; }
          .an-ptable td { padding: 3px 6px; border: 1px solid #D0D5DD; }
          .an-ptable td.num, .an-ptable th.num { text-align: right; }
          .an-ptable tr { break-inside: avoid; page-break-inside: avoid; }
          .an-ptable thead { display: table-header-group; }
          .an-red { color: #B42318; font-weight: 700; }
          .an-green { color: #027A48; font-weight: 700; }
        }
        .an-print-head { display: none; }
        .an-print-head h2 { margin:0 0 2px; font-size:19px; font-weight:800; color:#1E3A5F; }
        .an-print-head p { margin:0 0 14px; font-size:11px; color:#667085; }
      `}</style>

      <div className="an-print-head">
        <h2>{employee ? `Task Report — ${employee.name}` : "Team Task Report"}</h2>
        <p>
          {employee ? `Role: ${({assigned:"Assignee",supervising:"Supervisor",created:"Creator"})[activeRole]} · ` : ""}
          Generated {fmtDate(Date.now())} · Aquint Task Management
        </p>
      </div>

      <div className="page-header an-no-print" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12, marginBottom:20 }}>
        <h1 style={{ margin:0 }}>🔍 Analyze</h1>
        {(employee ? total > 0 : teamStats.rows.length > 0) && (
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <label className="an-no-print" style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, color:"var(--text-tertiary)" }}>
              <input type="checkbox" checked={pdfFullList} onChange={e=>setPdfFullList(e.target.checked)} />
              full task list
            </label>
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>🖨 Download PDF</button>
            <button type="button" className="btn" onClick={handleExport}>⬇ Export Excel</button>
          </div>
        )}
      </div>

      <div className="card an-no-print" style={{ overflow:"visible", marginBottom:20 }}>
        <div className="card-body" style={{ maxWidth:460 }}>
          <CustomSelect
            label="Employee"
            placeholder="-- Select an employee --"
            options={larkUsers}
            value={employeeId}
            onChange={val => { setEmployeeId(val); setRole("auto"); }}
            searchable={true}
            isClearable={true}
          />
        </div>
      </div>

      {!employee ? (
        <>
          <div className="an-kpis">
            <div className="an-kpi"><div className="an-kpi-num">{teamStats.totalTasks.toLocaleString()}</div>
              <div className="an-kpi-lab">Tasks</div><div className="an-kpi-sub">across the whole team</div></div>
            <div className="an-kpi"><div className="an-kpi-num" style={{color:COLORS.Done}}>{teamStats.overall.Done.toLocaleString()}</div>
              <div className="an-kpi-lab">Completed</div>
              <div className="an-kpi-sub">{teamStats.totalTasks?((teamStats.overall.Done/teamStats.totalTasks)*100).toFixed(1):0}% of total</div></div>
            <div className="an-kpi"><div className="an-kpi-num" style={{color:COLORS.Overdue}}>{teamStats.overall.Overdue.toLocaleString()}</div>
              <div className="an-kpi-lab">Overdue</div><div className="an-kpi-sub">open, past due</div></div>
            <div className="an-kpi"><div className="an-kpi-num" style={{color:COLORS["In Progress"]}}>
              {(teamStats.overall["Not Started"]+teamStats.overall["In Progress"]).toLocaleString()}</div>
              <div className="an-kpi-lab">Pending</div><div className="an-kpi-sub">not yet due</div></div>
            <div className="an-kpi"><div className="an-kpi-num">{teamStats.rows.length}</div>
              <div className="an-kpi-lab">People</div><div className="an-kpi-sub">with tasks</div></div>
          </div>

          <div className="an-grid" style={{ marginBottom:16 }}>
            <div className="an-card">
              <h3>Status mix — whole team</h3>
              <div className="an-donut-wrap">
                <Donut data={Object.keys(COLORS).map(k=>({label:k,value:teamStats.overall[k]||0,color:COLORS[k]}))} />
                <div className="an-legend">
                  {Object.keys(COLORS).map(k=>(
                    <div className="an-leg" key={k}>
                      <span className="an-dot" style={{background:COLORS[k]}} />
                      <span className="an-leg-lab">{k}</span>
                      <span className="an-leg-val">{(teamStats.overall[k]||0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="an-card">
              <h3>Most overdue</h3>
              <HBars items={[...teamStats.rows].sort((a,b)=>b.overdue-a.overdue).slice(0,8)
                .map(u=>({label:u.name,value:u.overdue,color:COLORS.Overdue}))} />
            </div>
            <div className="an-card">
              <h3>Busiest (assigned tasks)</h3>
              <HBars items={teamStats.rows.slice(0,8).map(u=>({label:u.name,value:u.assigned}))} />
            </div>
          </div>

          <div className="an-card">
            <h3>Every employee — click a row to drill in</h3>
            <div style={{ overflowX:"auto" }}>
              <table className="an-table">
                <thead><tr>
                  <th style={{textAlign:"left"}}>Employee</th><th>Assigned</th><th>Done</th>
                  <th>Overdue</th><th>Pending</th><th>On time</th><th>Supervising</th><th>Created</th>
                </tr></thead>
                <tbody>
                  {teamStats.rows.map(u=>{
                    const rated=u.onTime+u.late;
                    return (
                      <tr key={u.id} onClick={()=>{setEmployeeId(u.id); setRole("auto");}}>
                        <td style={{textAlign:"left",fontWeight:700}}>{u.name}</td>
                        <td>{u.assigned.toLocaleString()}</td>
                        <td style={{color:COLORS.Done,fontWeight:700}}>{u.done.toLocaleString()}</td>
                        <td style={{color:u.overdue?COLORS.Overdue:"inherit",fontWeight:u.overdue?700:400}}>{u.overdue.toLocaleString()}</td>
                        <td>{u.pending.toLocaleString()}</td>
                        <td>{rated?`${((u.onTime/rated)*100).toFixed(0)}%`:"—"}</td>
                        <td>{u.supervising.toLocaleString()}</td>
                        <td>{u.created.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="an-roles an-no-print">
            {[["assigned","📋 Assigned"],["supervising","👁️ Supervising"],["created","📝 Created"]].map(([k,lab]) => (
              <button key={k} type="button" className={`an-role ${activeRole===k?"active":""}`} onClick={()=>setRole(k)}>
                {lab}<span className="an-role-n">{roleSets[k].length.toLocaleString()}</span>
              </button>
            ))}
          </div>

          {total === 0 ? (
            <div style={{ textAlign:"center", padding:"50px 20px", color:"var(--text-disabled)" }}>
              <p style={{ fontSize:14, fontWeight:600 }}>No tasks for {employee.name} in this role.</p>
            </div>
          ) : (
            <>
              <div className="an-kpis">
                <div className="an-kpi"><div className="an-kpi-num">{total.toLocaleString()}</div><div className="an-kpi-lab">Tasks</div></div>
                <div className="an-kpi"><div className="an-kpi-num" style={{color:COLORS.Done}}>{doneN.toLocaleString()}</div>
                  <div className="an-kpi-lab">Completed</div><div className="an-kpi-sub">{total?((doneN/total)*100).toFixed(1):0}% of total</div></div>
                <div className="an-kpi"><div className="an-kpi-num" style={{color:COLORS.Overdue}}>{stats.byStatus.Overdue.toLocaleString()}</div>
                  <div className="an-kpi-lab">Overdue</div><div className="an-kpi-sub">open, past due</div></div>
                <div className="an-kpi"><div className="an-kpi-num" style={{color:COLORS["In Progress"]}}>{pending.toLocaleString()}</div>
                  <div className="an-kpi-lab">Pending</div><div className="an-kpi-sub">not yet due</div></div>
                <div className="an-kpi"><div className="an-kpi-num">{rated?((stats.onTime/rated)*100).toFixed(0):0}%</div>
                  <div className="an-kpi-lab">On time</div><div className="an-kpi-sub">{stats.onTime.toLocaleString()} of {rated.toLocaleString()} timed</div></div>
              </div>

              <div className="an-grid">
                <div className="an-card">
                  <h3>Status mix</h3>
                  <div className="an-donut-wrap">
                    <Donut data={Object.keys(COLORS).map(k=>({label:k,value:stats.byStatus[k]||0,color:COLORS[k]}))} />
                    <div className="an-legend">
                      {Object.keys(COLORS).map(k=>(
                        <div className="an-leg" key={k}>
                          <span className="an-dot" style={{background:COLORS[k]}} />
                          <span className="an-leg-lab">{k}</span>
                          <span className="an-leg-val">{(stats.byStatus[k]||0).toLocaleString()}</span>
                          <span style={{fontSize:11,color:"var(--text-tertiary)",width:44,textAlign:"right"}}>
                            {total?(((stats.byStatus[k]||0)/total)*100).toFixed(1):0}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="an-card">
                  <h3>On time vs late</h3>
                  <HBars items={[
                    { label:"On time", value:stats.onTime, color:COLORS.Done },
                    { label:"Late", value:stats.late, color:COLORS.Overdue },
                  ]} />
                  <h3 style={{marginTop:18}}>How late</h3>
                  <HBars items={Object.entries(stats.lateBuckets).map(([k,v])=>({label:k,value:v,color:"#F79009"}))} />
                </div>

                <div className="an-card">
                  <h3>By department</h3>
                  <HBars items={deptItems} />
                </div>

                <div className="an-card">
                  <h3>By priority</h3>
                  <HBars items={Object.entries(stats.byPriority).sort((a,b)=>b[1]-a[1]).map(([k,v])=>({label:k,value:v}))} />
                  <h3 style={{marginTop:18}}>By type</h3>
                  <HBars items={Object.entries(stats.byType).sort((a,b)=>b[1]-a[1]).map(([k,v])=>({label:k,value:v,color:"var(--purple)"}))} />
                </div>

                <div className="an-card" style={{ gridColumn:"1 / -1" }}>
                  <h3>Monthly trend — due vs completed vs overdue</h3>
                  <VBars groups={months.map(m=>({
                    label:m.slice(2),
                    bars:[
                      {label:"Due",value:stats.byMonth[m].due,color:"var(--primary)"},
                      {label:"Completed",value:stats.byMonth[m].done,color:COLORS.Done},
                      {label:"Overdue",value:stats.byMonth[m].overdue,color:COLORS.Overdue},
                    ],
                  }))} />
                  <div style={{display:"flex",gap:16,marginTop:12,fontSize:11.5,color:"var(--text-tertiary)",fontWeight:600}}>
                    <span><span className="an-dot" style={{background:"var(--primary)",display:"inline-block",marginRight:5}} />Due</span>
                    <span><span className="an-dot" style={{background:COLORS.Done,display:"inline-block",marginRight:5}} />Completed</span>
                    <span><span className="an-dot" style={{background:COLORS.Overdue,display:"inline-block",marginRight:5}} />Overdue</span>
                  </div>
                </div>
              </div>

              {/* ── PDF-only pages: the same eight sheets as the Excel export ── */}
              <div className="an-print-only">
                {(() => {
                  const pctOf = (n, d) => (d ? `${((n / d) * 100).toFixed(1)}%` : "—");
                  const byNewest = (a, b) => (b.end || 0) - (a.end || 0);
                  const ov = stats.detail.filter(t => t.status === "Overdue").sort(byNewest);
                  const pd = stats.detail.filter(t => t.status === "Not Started" || t.status === "In Progress").sort(byNewest);
                  const all = [...stats.detail].sort(byNewest);
                  const months = Object.keys(stats.byMonth).sort();
                  const Sheet = ({ title, sub, children }) => (
                    <section className="an-sheet"><h4>{title}</h4>{sub && <p className="sub">{sub}</p>}{children}</section>
                  );
                  const TaskTableP = ({ list, extraHead, extraVal, showStatus }) => (
                    <table className="an-ptable">
                      {/* fixed widths — without these a long task name pushes the
                          right-hand columns off the edge of the page */}
                      <colgroup>
                        <col style={{ width: showStatus ? "11%" : "12%" }} />
                        <col style={{ width: showStatus ? "28%" : "33%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "11%" }} />
                        {showStatus && <col style={{ width: "10%" }} />}
                        <col style={{ width: showStatus ? "10%" : "12%" }} />
                      </colgroup>
                      <thead><tr>
                        <th>Task ID</th><th>Task</th><th>Assigned Users</th><th>Priority</th>
                        <th className="num">Due date</th><th className="num">{extraHead}</th>
                        {showStatus && <th>Status</th>}<th className="num">Done</th>
                      </tr></thead>
                      <tbody>
                        {list.map((t, i) => (
                          <tr key={i}>
                            <td>{t.id}</td><td>{t.name}</td><td>{t.assignees}</td><td>{t.priority}</td>
                            <td className="num">{fmtDate(t.end)}</td>
                            <td className="num">{extraVal(t)}</td>
                            {showStatus && <td>{t.status}</td>}
                            <td className="num">{t.ratio}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                  return (
                    <>
                      <Sheet title="Summary">
                        <table className="an-ptable">
                          <thead><tr><th>Metric</th><th className="num">Value</th><th className="num">Share</th></tr></thead>
                          <tbody>
                            <tr><td>Tasks</td><td className="num">{total.toLocaleString()}</td><td className="num"></td></tr>
                            <tr><td>Completed</td><td className="num an-green">{doneN.toLocaleString()}</td><td className="num">{pctOf(doneN,total)}</td></tr>
                            <tr><td>Overdue (open, past due)</td><td className="num an-red">{stats.byStatus.Overdue.toLocaleString()}</td><td className="num">{pctOf(stats.byStatus.Overdue,total)}</td></tr>
                            <tr><td>Pending (not yet due)</td><td className="num">{pending.toLocaleString()}</td><td className="num">{pctOf(pending,total)}</td></tr>
                            <tr><td>— of completed: on time</td><td className="num">{stats.onTime.toLocaleString()}</td><td className="num">{pctOf(stats.onTime,rated)}</td></tr>
                            <tr><td>— of completed: late</td><td className="num">{stats.late.toLocaleString()}</td><td className="num">{pctOf(stats.late,rated)}</td></tr>
                            <tr><td>— completion time not recorded</td><td className="num">{(doneN-rated).toLocaleString()}</td><td className="num"></td></tr>
                          </tbody>
                        </table>
                      </Sheet>

                      <Sheet title="Timeliness" sub={`On time ${stats.onTime.toLocaleString()} · Late ${stats.late.toLocaleString()} · of ${rated.toLocaleString()} timed`}>
                        <table className="an-ptable">
                          <thead><tr><th>Lateness</th><th className="num">Tasks</th><th className="num">Share of late</th></tr></thead>
                          <tbody>{Object.entries(stats.lateBuckets).map(([k,v])=>(
                            <tr key={k}><td>{k}</td><td className="num">{v.toLocaleString()}</td><td className="num">{pctOf(v,stats.late)}</td></tr>))}
                          </tbody>
                        </table>
                      </Sheet>

                      <Sheet title="By Department">
                        <table className="an-ptable">
                          <thead><tr><th>Department</th><th className="num">Total</th><th className="num">Done</th>
                            <th className="num">Overdue</th><th className="num">Pending</th><th className="num">% complete</th></tr></thead>
                          <tbody>{Object.entries(stats.byDept).sort((a,b)=>b[1].total-a[1].total).map(([k,v])=>(
                            <tr key={k}><td>{k}</td><td className="num">{v.total.toLocaleString()}</td>
                              <td className="num">{v.done.toLocaleString()}</td>
                              <td className={`num ${v.overdue?"an-red":""}`}>{v.overdue.toLocaleString()}</td>
                              <td className="num">{v.pending.toLocaleString()}</td>
                              <td className="num">{pctOf(v.done,v.total)}</td></tr>))}
                          </tbody>
                        </table>
                      </Sheet>

                      <Sheet title="Priority & Type">
                        <table className="an-ptable" style={{marginBottom:"6mm"}}>
                          <thead><tr><th>Priority</th><th className="num">Tasks</th></tr></thead>
                          <tbody>{Object.entries(stats.byPriority).sort((a,b)=>b[1]-a[1]).map(([k,v])=>(
                            <tr key={k}><td>{k}</td><td className="num">{v.toLocaleString()}</td></tr>))}</tbody>
                        </table>
                        <table className="an-ptable">
                          <thead><tr><th>Task type</th><th className="num">Tasks</th></tr></thead>
                          <tbody>{Object.entries(stats.byType).sort((a,b)=>b[1]-a[1]).map(([k,v])=>(
                            <tr key={k}><td>{k}</td><td className="num">{v.toLocaleString()}</td></tr>))}</tbody>
                        </table>
                      </Sheet>

                      <Sheet title="Monthly Trend" sub="By due date">
                        <table className="an-ptable">
                          <thead><tr><th>Month</th><th className="num">Tasks due</th><th className="num">Completed</th><th className="num">Overdue</th></tr></thead>
                          <tbody>{months.map(m=>(
                            <tr key={m}><td>{m}</td><td className="num">{stats.byMonth[m].due.toLocaleString()}</td>
                              <td className="num">{stats.byMonth[m].done.toLocaleString()}</td>
                              <td className="num">{stats.byMonth[m].overdue.toLocaleString()}</td></tr>))}</tbody>
                        </table>
                      </Sheet>

                      <Sheet title={`Overdue — ${ov.length.toLocaleString()} needing attention`} sub="Past due and not yet complete. Most recent due date first.">
                        <TaskTableP list={ov} extraHead="Days late" extraVal={t=>t.daysOverdue} />
                      </Sheet>

                      <Sheet title={`Pending — ${pd.length.toLocaleString()} not yet due`} sub="Not complete and not yet past due. Most recent due date first.">
                        <TaskTableP list={pd} extraHead="Days left" extraVal={t=>t.daysLeft} showStatus />
                      </Sheet>

                      {pdfFullList && (
                        <Sheet title={`All Tasks — ${all.length.toLocaleString()}`} sub="Every task in this role, most recent due date first.">
                          <TaskTableP list={all} extraHead="Completed" extraVal={t=>fmtDate(t.comp)} showStatus />
                        </Sheet>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
